import React, { useEffect, useState } from "react";
import {
    Button,
    Checkbox,
    Chip,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    Typography,
    CircularProgress,
    FormControlLabel,
    RadioGroup,
    Radio,
} from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import SearchOffIcon from "@material-ui/icons/SearchSharp";
import styled from "styled-components";
import BatchPrint from "../../components/BatchPrint";
import { toast } from "react-toastify";
import EventApi from "../../services/event";
import { statusEnum } from "../../config/status";
import { config } from "../../config";
import { getTagConfig } from "../../utils/tagConfig";

/* 🎨 Estilos */
const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
`;

const ControlsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fafafa;
    padding: 8px 16px;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    position: sticky;
    top: 0;
    z-index: 10;
`;

const ControlGroup = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const StyledList = styled(List)`
    background: white;
    border-radius: 12px;
    padding: 8px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
`;

const StyledListItem = styled(ListItem)`
    cursor: pointer;
    border-radius: 8px;
    &:hover {
        background: #f5f5f5;
    }
`;

const EmptyState = styled.div`
    text-align: center;
    margin-top: 48px;
    color: #666;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const LoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
`;

const fieldNames = {
    name: "Nome",
    company: "Empresa",
    role: "Função",
    qrcode: "QRCODE",
};

const CredentialPrinter = () => {
    // Extract eventId from URL query parameters (fallback to 1)
    const urlParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
    const eventId = React.useMemo(() => parseInt(urlParams.get("event_id") || urlParams.get("eventId") || "1", 10), [urlParams]);
    const tagConfig = React.useMemo(() => getTagConfig(eventId), [eventId]);

    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedPeople, setSelectedPeople] = useState([]);
    const [loading, setIsLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [people, setPeople] = useState([]);
    const [statusValues, setStatusValues] = useState({});
    const [fields, setFields] = useState({
        name: true,
        company: true,
        role: false,
        qrcode: false,
    });
    const [direction, setDirection] = useState("left");
    const [qrcodeSize, setQrcodeSize] = useState("1");

    // Initialize fields checklist with values from the loaded tagConfig
    useEffect(() => {
        if (tagConfig) {
            setFields({
                name: tagConfig.showName ?? true,
                company: tagConfig.showCompany ?? true,
                role: tagConfig.showRole ?? false,
                qrcode: tagConfig.showQr ?? false,
            });
        }
    }, [tagConfig]);

    const handleSelectCategory = (event) => {
        setSelectedCategory(event.target.value);
        setSelectedPeople([]); // reseta seleção
    };

    const togglePerson = (id) => {
        setSelectedPeople((prev) => (prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]));
    };

    const handleToggleAll = () => {
        if (selectedPeople?.length === filteredPeople?.length) {
            setSelectedPeople([]);
        } else {
            setSelectedPeople(filteredPeople?.map((p) => p?.id));
        }
    };

    const getRows = async () => {
        try {
            setIsLoading(true);

            const [respEvent, statusResp] = await Promise.all([
                EventApi.getAllPersonByEvent(eventId, {}),
                EventApi.getStatus(),
            ]);

            if (!respEvent || !statusResp.data) {
                throw new Error("Failed to load data");
            }

            const status = Object.fromEntries(statusResp.data.data.map((item) => [item.id, item]));
            setStatusValues(status);

            const formatedList = respEvent.data.data.map((item) => {
                const isCredenciated = item.credential_generated;
                return {
                    ...item,
                    status: status[item.status_id]?.name || "",
                    statusName: statusEnum[status[item.status_id]?.name] || "",
                    positionValue: item.position?.value || "",
                    isVip: item.vip ? "Sim" : "Não",
                    isCredenciated: isCredenciated ? "Sim" : "Não",
                };
            });

            setPeople(formatedList);

            const counts = formatedList.reduce((acc, p) => {
                if (p.event_category) {
                    acc[p.event_category] = (acc[p.event_category] || 0) + 1;
                }
                return acc;
            }, {});
            setCategories(Object.entries(counts));

            setIsLoading(false);
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Erro ao carregar os dados");
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getRows();
    }, [eventId]);

    const filteredPeople = selectedCategory ? people?.filter((p) => p?.event_category === selectedCategory) : people;

    return (
        <Container>
            {/* Toolbar fixa */}
            <ControlsContainer>
                <ControlGroup>
                    <Select
                        value={selectedCategory}
                        onChange={handleSelectCategory}
                        displayEmpty
                        variant="outlined"
                        size="small"
                        style={{ minWidth: 220 }}
                    >
                        <MenuItem value="">
                            <em>Todas as categorias ({people?.length})</em>
                        </MenuItem>
                        {categories.map(([cat, count], idx) => (
                            <MenuItem key={idx} value={cat}>
                                {cat} ({count})
                            </MenuItem>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <BatchPrint
                        people={filteredPeople.filter((p) => selectedPeople.includes(p.id))}
                        fields={{ ...fields, qrcodePosition: direction, qrcodeSize: qrcodeSize }}
                        eventId={eventId}
                    />

                    {filteredPeople?.length > 0 && (
                        <Button
                            startIcon={
                                selectedPeople?.length === filteredPeople?.length ? <ClearAllIcon /> : <CheckBoxIcon />
                            }
                            variant="outlined"
                            size="small"
                            onClick={handleToggleAll}
                        >
                            {selectedPeople?.length === filteredPeople?.length ? "Desmarcar todos" : "Selecionar todos"}
                        </Button>
                    )}
                </ControlGroup>
            </ControlsContainer>

            {/* Configuração de campos da etiqueta */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Campos da Etiqueta
                </Typography>
                {fields && Object.entries(fields).map(([key, value]) => (
                    <FormControlLabel
                        key={key}
                        control={
                            <Checkbox
                                checked={value}
                                onChange={(e) =>
                                    setFields({
                                        ...fields,
                                        [key]: e.target.checked,
                                    })
                                }
                                color="primary"
                            />
                        }
                        label={fieldNames[key]}
                    />
                ))}
                {fields?.qrcode ? (
                    <div style={{ display: "flex", gap: "10px" }}>
                        <div>
                            <Typography variant="subtitle1" gutterBottom>
                                Local do Qrcode
                            </Typography>
                            <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                value={direction}
                                name="radio-buttons-group"
                                onChange={(e) => setDirection(e.target.value)}
                            >
                                <FormControlLabel value="left" control={<Radio />} label="Esquerda" />
                                <FormControlLabel value="right" control={<Radio />} label="Direita" />
                            </RadioGroup>
                        </div>
                        <div>
                            <Typography variant="subtitle1" gutterBottom>
                                Tamanho do Qrcode
                            </Typography>
                            <RadioGroup
                                aria-labelledby="demo-radio-buttons-group-label"
                                value={qrcodeSize}
                                name="radio-buttons-group"
                                onChange={(e) => setQrcodeSize(e.target.value)}
                            >
                                <FormControlLabel value="1" control={<Radio />} label="1" />
                                <FormControlLabel value="2" control={<Radio />} label="2" />
                                <FormControlLabel value="3" control={<Radio />} label="3" />
                            </RadioGroup>
                        </div>
                    </div>
                ) : null}
            </div>

            <div
                style={{
                    marginTop: 16,
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <div
                    className="printable-tag"
                    style={{
                        width: `${tagConfig.widthMm}mm`,
                        height: `${tagConfig.heightMm}mm`,
                        position: "relative",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        backgroundColor: "#ffffff",
                        border: "1px dashed #78909c",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                    }}
                >
                    {/* Elemento: QR Code */}
                    {fields?.qrcode && (
                        <div
                            style={{
                                position: "absolute",
                                top: `${tagConfig.qrCodePosition?.top || 0}mm`,
                                left: `${tagConfig.qrCodePosition?.left || 0}mm`,
                                width: `${tagConfig.qrCodeSizeMm || 25}mm`,
                                height: `${tagConfig.qrCodeSizeMm || 25}mm`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={`${config.QRCODE_HOST || "https://api.qrserver.com/v1/create-qr-code"}/?size=150x150&data=0001`}
                                alt="QR Code"
                                style={{ width: "100%", height: "100%" }}
                            />
                        </div>
                    )}

                    {/* Elemento: Nome */}
                    {fields?.name && (
                        <div
                            style={{
                                position: "absolute",
                                top: `${tagConfig.namePosition?.top || 0}mm`,
                                left: `${tagConfig.namePosition?.left || 0}mm`,
                                fontSize: `${tagConfig.nameFontSize || 14}pt`,
                                fontWeight: "700",
                                fontFamily: "'Inter', 'Roboto', sans-serif",
                                color: "#000000",
                                whiteSpace: "nowrap",
                                lineHeight: "1.2",
                            }}
                        >
                            Fulano da Silva
                        </div>
                    )}

                    {/* Elemento: Empresa */}
                    {fields?.company && (
                        <div
                            style={{
                                position: "absolute",
                                top: `${tagConfig.companyPosition?.top || 0}mm`,
                                left: `${tagConfig.companyPosition?.left || 0}mm`,
                                fontSize: `${tagConfig.companyFontSize || 12}pt`,
                                fontWeight: "600",
                                fontFamily: "'Inter', 'Roboto', sans-serif",
                                color: "#37474f",
                                whiteSpace: "nowrap",
                                lineHeight: "1.2",
                            }}
                        >
                            Empresa XPTO
                        </div>
                    )}

                    {/* Elemento: Cargo */}
                    {fields?.role && (
                        <div
                            style={{
                                position: "absolute",
                                top: `${tagConfig.rolePosition?.top || 0}mm`,
                                left: `${tagConfig.rolePosition?.left || 0}mm`,
                                fontSize: `${tagConfig.roleFontSize || 10}pt`,
                                fontStyle: "italic",
                                fontFamily: "'Inter', 'Roboto', sans-serif",
                                color: "#546e7a",
                                whiteSpace: "nowrap",
                                lineHeight: "1.2",
                            }}
                        >
                            Diretor de Marketing
                        </div>
                    )}
                </div>
            </div>

            {/* Lista ou estado vazio */}
            {filteredPeople?.length === 0 ? (
                <EmptyState>
                    <SearchOffIcon style={{ fontSize: 48, opacity: 0.6 }} />
                    <Typography variant="body1" color="textSecondary">
                        Nenhuma pessoa encontrada nesta categoria
                    </Typography>
                </EmptyState>
            ) : (
                <StyledList>
                    {filteredPeople?.map((p) => (
                        <StyledListItem key={p.id} onClick={() => togglePerson(p.id)}>
                            <Checkbox
                                checked={selectedPeople.includes(p.id)}
                                edge="start"
                                tabIndex={-1}
                                disableRipple
                            />
                            <ListItemText
                                primary={p?.name}
                                secondary={
                                    <>
                                        {p?.event_category}
                                        <Chip
                                            label={p?.statusName}
                                            size="small"
                                            style={{ marginLeft: 8 }}
                                            color={p?.statusName === "Credenciado" ? "success" : "default"}
                                        />
                                    </>
                                }
                            />
                        </StyledListItem>
                    ))}
                </StyledList>
            )}

            {/* Feedback de carregamento global */}
            {loading && (
                <LoadingOverlay>
                    <CircularProgress />
                    <Typography variant="body2" style={{ marginTop: 8 }}>
                        Preparando impressão…
                    </Typography>
                </LoadingOverlay>
            )}
        </Container>
    );
};

export default CredentialPrinter;
